import React, { useState, useEffect } from "react";
import { updatePreference } from '../services/api';
import '../styles/movieCard.css';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { byPrefixAndName } from '@fortawesome/fontawesome-svg-core/import.macro';

const MovieCard = ({ movie, onPreferenceChange }) => {
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isPVODInfoOpen, setIsPVODInfoOpen] = useState(false);

  const [isSynopsisOpen, setIsSynopsisOpen] = useState(false);
  const [isMoviePosterOpen, SetIsMoviePosterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 645 : false);

  //Close popup when Escape key is pressed
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setIsTrailerOpen(false);
        setIsSynopsisOpen(false);
        SetIsMoviePosterOpen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 645);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePreferenceChange = async (preference) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const result = await updatePreference(movie.imdb_id, preference);

      if (result.Code === 111) {
        // Call the parent callback to update the movie state
        onPreferenceChange(movie.imdb_id, preference, result.Data);
      } else {
        console.error("Failed to update preference:", result.Message);
      }
    } catch (error) {
      console.error("Error updating preference:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStreamingIcons = (dist) => {
    if (!dist) return null;

    const services = {
      Hulu: {
        icon: "fak fa-hulu-inverse fa-3x",
        color: "#66aa33",
        url: "https://www.hulu.com/",
        title: "Hulu",
      },
      Netflix: {
        icon: "fak fa-netflix-inverse fa-3x",
        color: "#e50914",
        url: "https://www.netflix.com/",
        title: "Netflix",
      },
      "HBO Max": {
        icon: "fak fa-hbomax-inverse fa-3x",
        color: "#991eeb",
        url: "https://www.hbomax.com/",
        title: "HBO Max",
      },
      "Disney+": {
        icon: "fak fa-disney-plus-inverse fa-3x",
        color: "#113CCF",
        url: "https://www.disneyplus.com/",
        title: "Disney+",
      },
      Peacock: {
        icon: "fak fa-peacock-inverse fa-3x",
        color: "#000000",
        url: "https://www.peacocktv.com/",
        title: "Peacock",
      },
      "Apple TV+": {
        icon: "fak fa-apple-tv-plus-inverse fa-3x",
        color: "#1d1d1f",
        url: "https://www.apple.com/apple-tv-plus/",
        title: "Apple TV+",
      },
      Theaters: {
        icon: "fak fa-movie-ticket fa-3x",
        color: "#000000",
        url: "https://www.fandango.com/",
        title: "In Theaters",
      },
      "Amazon Prime": {
        icon: "fak fa-prime-video-inverse fa-3x",
        color: "#00a8e1",
        url: "https://www.amazon.com/gp/video/offers/",
        title: "Amazon Prime Video",
      },
      'Your favorite On-Demand service': { 
        icon: 'fa-solid fa-circle-dollar fa-3x', 
        color: '#000000', 
        url: null,
        title: "Your favorite On-Demand service",
      },
      "Paramount+": {
        icon: "fak fa-paramount-plus-inverse fa-3x",
        color: "#2965f1",
        url: "https://www.paramountplus.com/",
        title: "Paramount+",
      },
      "Roku TV": {
        icon: "fak fa-roku-inverse fa-3x",
        color: "#662D91",
        url: "https://therokuchannel.roku.com/",
        title: "Roku TV",
      },
      Showtime: {
        icon: "fak fa-showtime fa-3x",
        color: "#cf1e1e",
        url: "https://www.sho.com/",
        title: "Showtime",
      },
      Starz: {
        icon: "fak fa-starz-inverse fa-3x",
        color: "#000000",
        url: "https://www.starz.com/",
        title: "Starz",
      },
      "AMC+": {
        icon: "fak fa-amc-plus-inverse fa-3x",
        color: "#000000",
        url: "https://www.amcplus.com/",
        title: "AMC+",
      },
    };

    return dist.split(", ").map((service, index) => {
      const serviceName = service.trim();
      const serviceInfo = services[serviceName];
      if (!serviceInfo) return null;
      
      const iconElement = (
        <i
          className={serviceInfo.icon}
          style={{
            margin: "3px 3px 3px 3px",
            color: serviceInfo.color,
            opacity: 0.8,
          }}
          title={serviceInfo.title}
        />
      );

      // PVOD (no external link) → Show info modal on click
      const isPVOD = serviceName === 'Your favorite On-Demand service';
      if (!serviceInfo.url) {
        return (
          <span
            key={index}
            className={`streaming-icon ${isPVOD ? 'pvod-icon' : ''}`}
            role={isPVOD ? 'button' : undefined}
            aria-label={isPVOD ? 'Paid Video On Demand info' : undefined}
            onClick={isPVOD ? () => setIsPVODInfoOpen(true) : undefined}
          >
            {iconElement}
          </span>
        );
      }

      return (
        <a
          key={index}
          href={serviceInfo.url}
          target="_blank"
          rel="noopener noreferrer"
          className="streaming-icon"
          title={serviceInfo.title}
        >
          {iconElement}
        </a>
      );
    });
  };

  const isWatched = movie.watched || movie.seen_and_liked || movie.seen_and_disliked;
  const isFavorite = movie.isfavorite;
  const isNotInterested = movie.not_interested;
  const isLiked = movie.seen_and_liked;
  const isDisliked = movie.seen_and_disliked;

  return (
    <div className="movie-card">
      <div className="movie-card-content">
        {/* LEFT SIDE: Poster and Trailer Button */}
        <div className="movie-poster-section">
          {!imageError && movie.imdb_poster ? (
            <img
              src={movie.imdb_poster}
              alt={movie.title}
              className={`movie-poster${isMobile ? ' no-hover' : ''}`}
              loading="lazy"
              onError={() => setImageError(true)}
              onClick={isMobile ? undefined : () => SetIsMoviePosterOpen(true)}
            />
          ) : (
            <div className="movie-poster placeholder-image">
              <div className="poster-placeholder-content">
                <i className="fa-solid fa-image" style={{ fontSize: "2rem", color: "#a0aec0", marginBottom: "0.5rem" }}></i>
                <div style={{ fontSize: "0.875rem", color: "#718096", textAlign: "center", lineHeight: "1.4" }}>{movie.title}</div>
              </div>
            </div>
          )}
          <button className="trailer-btn" onClick={() => setIsTrailerOpen(true)} disabled={!movie.trailer_link}>
            Watch Trailer
            <i className="fa-solid fa-circle-play" style={{ margin: "0px 0px 0px 5px" }}></i>
          </button>
        </div>

        {/* MIDDLE: Movie Info and Streaming */}
        <div className="movie-info">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <h3 className="movie-title">{movie.title}</h3>
            <span
              onClick={(e) => {
                e.stopPropagation();
                setIsSynopsisOpen(true);
              }}
              style={{ cursor: "pointer", display: "inline-block" }}
              title="Show synopsis"
            >
              <i className="fa-regular fa-circle-info" style={{ fontSize: "1.1rem", color: "#000" }} />
            </span>
          </div>

          {movie.stars && (
            <p className="movie-stars">
              <strong>Starring:</strong> {movie.stars}
            </p>
          )}

          <div className="movie-meta">
            <span className="movie-type">
              {movie.series === "1" ? (
                <i className="fa-solid fa-tv" style={{ color: "#009932" }}></i>
              ) : movie.movie === "1" ? (
                <i className="fa-solid fa-camera-movie" style={{ color: "#009932" }}></i>
              ) : (
                ""
              )}
            </span>
            <span className="movie-genre">
              {movie.genre}
              {movie.mpaa && ` (${movie.mpaa})`}
            </span>
          </div>

          {/* Streaming Services */}
          <div className="streaming-section">
            <p className="streaming-label">Where to watch:</p>
            <div className="streaming-icons">
              {movie.dist ? (
                getStreamingIcons(movie.dist)
              ) : (
                <i
                  className="fa fa-face-sleeping fa-3x"
                  title="Not currently streaming"
                  style={{
                    margin: "3px 3px 3px 3px",
                    color: "#FFBA47",
                    opacity: 0.8,
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: Action Buttons */}
        <div className="action-buttons">
          <button
            className={`action-btn ${isFavorite ? "selected" : ""}`}
            onClick={() => handlePreferenceChange(isFavorite ? -1 : 1)}
            disabled={isLoading}
          >
            {isFavorite ? "Added" : "Add to Watchlist"}
          </button>

          {isWatched ? (
            <div className="thumbs-buttons">
              <button
                className={`thumb-btn ${isLiked ? "selected" : ""}`}
                onClick={() => handlePreferenceChange(isLiked ? -4 : 4)}
                disabled={isLoading}
              >
                <i className="fa-solid fa-thumbs-up"></i>
              </button>
              <button
                className={`thumb-btn ${isDisliked ? "selected btn-thumb-down" : ""}`}
                onClick={() => handlePreferenceChange(isDisliked ? -5 : 5)}
                disabled={isLoading}
              >
                <i className="fa-solid fa-thumbs-down"></i>
              </button>
            </div>
          ) : (
            <button className="action-btn" onClick={() => handlePreferenceChange(2)} disabled={isLoading}>
              Seen it
            </button>
          )}

          <button
            className={`action-btn ${isNotInterested ? "selected" : ""}`}
            onClick={() => handlePreferenceChange(isNotInterested ? -3 : 3)}
            disabled={isLoading}
          >
            {isNotInterested ? "Remove from Not Interested" : "Not interested"}
          </button>
        </div>
      </div>

      {/* Trailer Modal */}
      {isTrailerOpen && movie.trailer_link && (
        <div className="trailer-modal-overlay" onClick={() => setIsTrailerOpen(false)}>
          <div className="trailer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trailer-modal-header">
              <h4>{movie.title}</h4>
              <button className="close-btn" onClick={() => setIsTrailerOpen(false)}>
                ×
              </button>
            </div>
            <div className="trailer-modal-body">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${movie.trailer_link}`}
                title="YouTube video player"
                frameBorder="0"
                allowFullScreen
                className="trailer-iframe"
              />
            </div>
          </div>
        </div>
      )}

      {/* Synopsis Modal */}
      {isSynopsisOpen && (
        <div className="synopsis-modal-overlay" onClick={() => setIsSynopsisOpen(false)} style={{ ['--groups-header-offset']: '72.5px' }}>
          <div className="synopsis-modal" onClick={(e) => e.stopPropagation()}>
            <div className="synopsis-modal-header">
              <h4>{movie.title}</h4>
              <button className="close-btn" onClick={() => setIsSynopsisOpen(false)}>
                ×
              </button>
            </div>
            <div className="synopsis-modal-body">
              <p>{movie.synopsis}</p>
            </div>
          </div>
        </div>
      )}

      {/* Movie poster Modal */}
      {isMoviePosterOpen && (
        <div className="poster-modal-overlay" onClick={() => SetIsMoviePosterOpen(false)}>
          <div className="poster-modal" onClick={(e) => e.stopPropagation()}>
            <button className="poster-btn" onClick={() => SetIsMoviePosterOpen(false)}>
              ×
            </button>
            <div className="poster-modal-body">
              <img src={movie.imdb_poster} alt={movie.title} loading="lazy" onError={() => setImageError(true)} />
            </div>
          </div>
        </div>
      )}

      {/* PVOD Info Modal */}
      {isPVODInfoOpen && (
        <div className="trailer-modal-overlay" onClick={() => setIsPVODInfoOpen(false)}>
          <div className="info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="info-modal-header">
              <h4>Paid Video On Demand</h4>
              <button 
                className="close-btn"
                onClick={() => setIsPVODInfoOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="info-modal-body">
              <p>
                This title is available via paid video on demand. You can rent or purchase it from various online services.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovieCard;